import logo from "./assets/logo.png";
import checkPng from "./assets/check.png";
import loadingGif from "./assets/giphy.webp";

import { WalletDialogButton } from "@solana/wallet-adapter-material-ui";
import { useWallet } from "@solana/wallet-adapter-react";
import { Snackbar, CircularProgress } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useState, useCallback, useEffect, useMemo } from "react";
import { sign } from "tweetnacl";
import bs58 from "bs58";
import axios from "axios";
import clsx from "clsx";
import { getParsedNftAccountsByOwner } from "@nfteyez/sol-rayz";
import { Swiper, SwiperSlide } from "swiper/react";
import validNfts from "./hashlist.json";

export default function Home(props) {
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: undefined,
  });
  const [signMessage, setSignMessage] = useState({
    show: false,
    loading: false,
  });
  const [activateTool, setActivateTool] = useState({
    wallet: "",
    key: "",
  });
  const [holdingNft, setHoldingNft] = useState({
    data: [],
    done: false,
    isHoldingEnough: false,
  });

  const wallet = useWallet();

  async function startNftChecking() {
    const nfts = (
      await getParsedNftAccountsByOwner({
        publicAddress: wallet.publicKey,
      })
    ).filter((nft) => {
      if (validNfts.includes(nft.mint)) {
        return true;
      }
      return false;
    });

    let result = [];

    for (const validNft of nfts) {
      const getNftMetadata = await axios(validNft.data.uri);

      if (getNftMetadata && getNftMetadata.data) {
        result.push({
          ...validNft,
          data: {
            ...validNft.data,
            name: getNftMetadata.data.name,
            number: Number(
              getNftMetadata.data.name.split(" ")[4].replace("#", "")
            ),
          },
        });
      }
    }

    result = result.sort((a, b) => a.data.number - b.data.number);

    setHoldingNft({
      ...holdingNft,
      data: result,
      done: true,
      isHoldingEnough: nfts.length >= 1,
    });
  }

  const nftsCheck = useCallback(async () => {
    const nftsFiltered = holdingNft.data.filter((nft) => {
      if (nft.data.imageUrl) {
        return false;
      }
      return true;
    });

    for (const star of nftsFiltered) {
      const getMetaData = await axios(star.data.uri);

      if (getMetaData && getMetaData.data && getMetaData.data.image) {
        const findIndex = holdingNft.data.findIndex(
          (holdingStar) => holdingStar.data.name === star.data.name
        );
        //console.log({ findIndex, holdingNft });
        if (findIndex >= 0) {
          setHoldingNft(
            (() => {
              let holdingNftCopy = { ...holdingNft };
              holdingNftCopy.data[findIndex].data.imageUrl =
                getMetaData.data.image;
              return holdingNftCopy;
            })()
          );
        }
      }
      await new Promise((res) => setTimeout(res, 500));
    }
  }, [holdingNft.data]);

  useEffect(() => {
    if (holdingNft.done) {
      nftsCheck();
    }
  }, [holdingNft.done]);

  useEffect(() => {
    if (wallet.connected) {
      setSignMessage({
        ...signMessage,
        show: true,
      });
      startNftChecking();
    }
  }, [wallet.connected]);

  const signMessageAndSendRequest = async () => {
    setSignMessage({ ...signMessage, loading: true });
    try {
      // `publicKey` will be null if the wallet isn't connected
      if (!wallet.publicKey) throw new Error("Wallet not connected!");
      // `signMessage` will be undefined if the wallet doesn't support it
      if (!wallet.signMessage)
        throw new Error("Wallet does not support message signing!");

      // Encode anything as bytes
      const message = new TextEncoder().encode("wagmi!");

      // Sign the bytes using the wallet
      const signature = await wallet.signMessage(message);

      // Verify that the bytes were signed using the private key that matches the known public key
      const verifySignature = sign.detached.verify(
        message,
        signature,
        wallet.publicKey.toBytes()
      );

      if (verifySignature) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/verify`,
          {
            wallet: wallet.publicKey.toBase58(),
            signature: bs58.encode(signature),
          }
        );

        if (
          response &&
          response.data &&
          response.data.key &&
          response.data.wallet
        ) {
          setSignMessage({ ...signMessage, loading: false });
          setActivateTool({
            wallet: response.data.wallet,
            key: response.data.key,
          });
        }

        setAlertState({
          open: true,
          message: `Signing Succesfull`,
          severity: "success",
        });
        return;
      }
      throw new Error("Invalid signature!");
    } catch (error) {
      setSignMessage({ ...signMessage, loading: false });
      if (error.isAxiosError) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          return setAlertState({
            open: true,
            message: `Signing failed: ${error.response.data.message}`,
            severity: "error",
          });
        }
      }
      setAlertState({
        open: true,
        message: `Signing failed: ${error?.message}`,
        severity: "error",
      });
    }
  };

  return (
    <div className="app">
      <header>
        <div className="header-top">
          <span className="logo-wrapper">
            <img src={logo} alt="" className="logo" />
            <h5>Star Scripts</h5>
          </span>
        </div>
        <div className="header-down">
          <div className="links">
            <a className="discord" href="#" target="_blank" rel="noreferrer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 30 30"
              >
                <path d="M25.12,6.946c-2.424-1.948-6.257-2.278-6.419-2.292c-0.256-0.022-0.499,0.123-0.604,0.357 c-0.004,0.008-0.218,0.629-0.425,1.228c2.817,0.493,4.731,1.587,4.833,1.647c0.478,0.278,0.638,0.891,0.359,1.368 C22.679,9.572,22.344,9.75,22,9.75c-0.171,0-0.343-0.043-0.501-0.135C21.471,9.598,18.663,8,15.002,8 C11.34,8,8.531,9.599,8.503,9.615C8.026,9.892,7.414,9.729,7.137,9.251C6.86,8.775,7.021,8.164,7.497,7.886 c0.102-0.06,2.023-1.158,4.848-1.65c-0.218-0.606-0.438-1.217-0.442-1.225c-0.105-0.235-0.348-0.383-0.604-0.357 c-0.162,0.013-3.995,0.343-6.451,2.318C3.564,8.158,1,15.092,1,21.087c0,0.106,0.027,0.209,0.08,0.301 c1.771,3.11,6.599,3.924,7.699,3.959c0.007,0.001,0.013,0.001,0.019,0.001c0.194,0,0.377-0.093,0.492-0.25l1.19-1.612 c-2.61-0.629-3.99-1.618-4.073-1.679c-0.444-0.327-0.54-0.953-0.213-1.398c0.326-0.443,0.95-0.541,1.394-0.216 C7.625,20.217,10.172,22,15,22c4.847,0,7.387-1.79,7.412-1.808c0.444-0.322,1.07-0.225,1.395,0.221 c0.324,0.444,0.23,1.066-0.212,1.392c-0.083,0.061-1.456,1.048-4.06,1.677l1.175,1.615c0.115,0.158,0.298,0.25,0.492,0.25 c0.007,0,0.013,0,0.019-0.001c1.101-0.035,5.929-0.849,7.699-3.959c0.053-0.092,0.08-0.195,0.08-0.301 C29,15.092,26.436,8.158,25.12,6.946z M11,19c-1.105,0-2-1.119-2-2.5S9.895,14,11,14s2,1.119,2,2.5S12.105,19,11,19z M19,19 c-1.105,0-2-1.119-2-2.5s0.895-2.5,2-2.5s2,1.119,2,2.5S20.105,19,19,19z"></path>
              </svg>
            </a>
            <a className="twitter" href="#" target="_blank" rel="noreferrer">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 30 30"
              >
                <path d="M28,6.937c-0.957,0.425-1.985,0.711-3.064,0.84c1.102-0.66,1.947-1.705,2.345-2.951c-1.03,0.611-2.172,1.055-3.388,1.295 c-0.973-1.037-2.359-1.685-3.893-1.685c-2.946,0-5.334,2.389-5.334,5.334c0,0.418,0.048,0.826,0.138,1.215 c-4.433-0.222-8.363-2.346-10.995-5.574C3.351,6.199,3.088,7.115,3.088,8.094c0,1.85,0.941,3.483,2.372,4.439 c-0.874-0.028-1.697-0.268-2.416-0.667c0,0.023,0,0.044,0,0.067c0,2.585,1.838,4.741,4.279,5.23 c-0.447,0.122-0.919,0.187-1.406,0.187c-0.343,0-0.678-0.034-1.003-0.095c0.679,2.119,2.649,3.662,4.983,3.705 c-1.825,1.431-4.125,2.284-6.625,2.284c-0.43,0-0.855-0.025-1.273-0.075c2.361,1.513,5.164,2.396,8.177,2.396 c9.812,0,15.176-8.128,15.176-15.177c0-0.231-0.005-0.461-0.015-0.69C26.38,8.945,27.285,8.006,28,6.937z"></path>
              </svg>
            </a>
          </div>
        </div>
      </header>
      <main className="container-lg">
        <div className="tool-access-wrapper">
          <h3 className="main-title">JOIN BETA</h3>
          <hr style={{ background: "#fff", height: 4, width: "100%" }} />
          <div className="step-1">
            <h6 className="title">
              CONNECT WALLET
              {wallet.connected && (
                <img className="checked" src={checkPng} alt="checked" />
              )}
            </h6>
            {wallet.connected ? (
              <>
                <span>{wallet.publicKey.toBase58()}</span>
                <Swiper
                  spaceBetween={20}
                  slidesPerView={3}
                  freeMode={true}
                  onSlideChange={() => console.log("slide change")}
                  onSwiper={(swiper) => console.log(swiper)}
                  className="nft-slider"
                  centerInsufficientSlides={true}
                >
                  {holdingNft.data.map((nft_) => {
                    return (
                      <SwiperSlide>
                        <div className="nft-card">
                          <div className="img">
                            <img
                              src={nft_.data.imageUrl || loadingGif}
                              alt="nft"
                            />
                          </div>
                          <div className="name">{nft_.data.name || ""}</div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </>
            ) : (
              <WalletDialogButton className="my-btn" />
            )}
          </div>
          {signMessage.show && holdingNft.done && (
            <>
              {holdingNft.isHoldingEnough ? (
                <div className="step-2">
                  <h6 className="title">
                    SIGN MESSAGE
                    {activateTool.key && (
                      <img className="checked" src={checkPng} alt="checked" />
                    )}
                  </h6>
                  <div
                    className={clsx({
                      "my-btn": true,
                      disabled: signMessage.loading || activateTool.key,
                    })}
                    onClick={signMessageAndSendRequest}
                  >
                    {signMessage.loading ? (
                      <CircularProgress style={{ width: 25, height: 25 }} />
                    ) : (
                      <span>SIGN</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="not-enough-nft">
                  <div>You need at least 1 test nft</div>
                  {/* <div className="me-buy-btn-wrapper">
                    <a
                      className="magiceden-button"
                      href="https://www.magiceden.io/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span>BUY ON MAGICEDEN</span>
                    </a>
                  </div> */}
                </div>
              )}
            </>
          )}
          {activateTool.key && activateTool.wallet && (
            <div className="step-3">
              <h6 className="title">ACTIVATE TOOL WITH THE KEY</h6>
              <span>{activateTool.key}</span>
            </div>
          )}
        </div>
      </main>
      <footer>
        <span className="footer-title">JOIN US</span>
        <div className="links">
          <a className="discord" href="#" target="_blank" rel="noreferrer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              viewBox="0 0 30 30"
            >
              <path d="M25.12,6.946c-2.424-1.948-6.257-2.278-6.419-2.292c-0.256-0.022-0.499,0.123-0.604,0.357 c-0.004,0.008-0.218,0.629-0.425,1.228c2.817,0.493,4.731,1.587,4.833,1.647c0.478,0.278,0.638,0.891,0.359,1.368 C22.679,9.572,22.344,9.75,22,9.75c-0.171,0-0.343-0.043-0.501-0.135C21.471,9.598,18.663,8,15.002,8 C11.34,8,8.531,9.599,8.503,9.615C8.026,9.892,7.414,9.729,7.137,9.251C6.86,8.775,7.021,8.164,7.497,7.886 c0.102-0.06,2.023-1.158,4.848-1.65c-0.218-0.606-0.438-1.217-0.442-1.225c-0.105-0.235-0.348-0.383-0.604-0.357 c-0.162,0.013-3.995,0.343-6.451,2.318C3.564,8.158,1,15.092,1,21.087c0,0.106,0.027,0.209,0.08,0.301 c1.771,3.11,6.599,3.924,7.699,3.959c0.007,0.001,0.013,0.001,0.019,0.001c0.194,0,0.377-0.093,0.492-0.25l1.19-1.612 c-2.61-0.629-3.99-1.618-4.073-1.679c-0.444-0.327-0.54-0.953-0.213-1.398c0.326-0.443,0.95-0.541,1.394-0.216 C7.625,20.217,10.172,22,15,22c4.847,0,7.387-1.79,7.412-1.808c0.444-0.322,1.07-0.225,1.395,0.221 c0.324,0.444,0.23,1.066-0.212,1.392c-0.083,0.061-1.456,1.048-4.06,1.677l1.175,1.615c0.115,0.158,0.298,0.25,0.492,0.25 c0.007,0,0.013,0,0.019-0.001c1.101-0.035,5.929-0.849,7.699-3.959c0.053-0.092,0.08-0.195,0.08-0.301 C29,15.092,26.436,8.158,25.12,6.946z M11,19c-1.105,0-2-1.119-2-2.5S9.895,14,11,14s2,1.119,2,2.5S12.105,19,11,19z M19,19 c-1.105,0-2-1.119-2-2.5s0.895-2.5,2-2.5s2,1.119,2,2.5S20.105,19,19,19z"></path>
            </svg>
          </a>
          <a className="twitter" href="#" target="_blank" rel="noreferrer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              viewBox="0 0 30 30"
            >
              <path d="M28,6.937c-0.957,0.425-1.985,0.711-3.064,0.84c1.102-0.66,1.947-1.705,2.345-2.951c-1.03,0.611-2.172,1.055-3.388,1.295 c-0.973-1.037-2.359-1.685-3.893-1.685c-2.946,0-5.334,2.389-5.334,5.334c0,0.418,0.048,0.826,0.138,1.215 c-4.433-0.222-8.363-2.346-10.995-5.574C3.351,6.199,3.088,7.115,3.088,8.094c0,1.85,0.941,3.483,2.372,4.439 c-0.874-0.028-1.697-0.268-2.416-0.667c0,0.023,0,0.044,0,0.067c0,2.585,1.838,4.741,4.279,5.23 c-0.447,0.122-0.919,0.187-1.406,0.187c-0.343,0-0.678-0.034-1.003-0.095c0.679,2.119,2.649,3.662,4.983,3.705 c-1.825,1.431-4.125,2.284-6.625,2.284c-0.43,0-0.855-0.025-1.273-0.075c2.361,1.513,5.164,2.396,8.177,2.396 c9.812,0,15.176-8.128,15.176-15.177c0-0.231-0.005-0.461-0.015-0.69C26.38,8.945,27.285,8.006,28,6.937z"></path>
            </svg>
          </a>
        </div>
      </footer>
      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert
          onClose={() => setAlertState({ ...alertState, open: false })}
          severity={alertState.severity}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
